/**
 * IIFE entry — built to dist/stoq.min.js with global `Stoq`.
 *
 * CDN head-embed usage:
 *
 *   <script src="https://cdn.example.com/stoq.min.js"
 *           data-shop="my-store.myshopify.com"
 *           defer></script>
 *
 * When `data-shop` is present the SDK auto-initializes on DOMContentLoaded
 * (immediately when the DOM is already ready) and exposes the resulting
 * client as `Stoq.client`. Without `data-shop` the script only defines the
 * global — call `Stoq.init({...})` yourself.
 *
 * Supported data attributes: data-shop, data-storefront-token (both
 * required for auto-init — all datasets are read with one Shopify
 * Storefront API call), data-host, data-market, data-locale,
 * data-storefront-api-version, data-namespace.
 *
 * Once the auto-initialized client is ready, `Stoq.openModal(options)` /
 * `Stoq.openInlineForm(options)` / `Stoq.removeInlineForm()` /
 * `Stoq.openPreorderModal(options)` / `Stoq.preorderButtonFor(variantId)` work
 * directly,
 * and a `window._RestockRocket` compatibility shim (theme-embed JavaScript
 * API: openModal / openInlineForm / removeInlineForm / getSellingPlan) is
 * installed — but only when the theme app embed has not already defined it
 * (the embed always wins).
 */
import { init } from './index'
import type { StoqClient } from './client'
import { defineStoqElements } from './elements'
import {
  removeInlineForm as removeInlineFormImpl,
  type InlineFormOptions,
  type ModalHandle,
  type ModalOptions,
  type ModalPrefill,
} from './modal'
import type {
  PreorderConfirmation,
  PreorderButtonContent,
  PreorderModalOptions,
} from './preorder'
import type { VariantInput } from './variant-input'
import type {
  NotifyMeWidgetOptions,
  PreorderBadgeWidgetOptions,
  PreorderButtonWidgetOptions,
  PreorderWidgetOptions,
  WidgetHandle,
} from './widgets'

export * from './index'

/** The auto-initialized client, set once auto-init completes. */
export let client: StoqClient | undefined

function requireClient(method: string): StoqClient {
  if (!client) {
    throw new Error(
      `[Stoq] ${method} called before the SDK finished initializing — `
      + 'wait for the stoq:loaded event (or use Stoq.init() and the returned client)',
    )
  }
  return client
}

/** Opens the notify-me modal using the auto-initialized client. */
export function openModal(options: ModalOptions): ModalHandle {
  return requireClient('openModal').openModal(options)
}

/** Renders the notify-me form inline using the auto-initialized client. */
export function openInlineForm(options: InlineFormOptions): ModalHandle | null {
  return requireClient('openInlineForm').openInlineForm(options)
}

/** Removes inline forms. Works even before init (it is client-independent). */
export function removeInlineForm(container?: Element | string): void {
  removeInlineFormImpl(container)
}

/** Opens the preorder modal using the auto-initialized client. */
export function openPreorderModal(
  options: PreorderModalOptions,
): Promise<PreorderConfirmation | null> {
  return requireClient('openPreorderModal').openPreorderModal(options)
}

/** Merchant-configured preorder CTA copy using the auto-initialized client. */
export function preorderButtonFor(variantId: number | string): PreorderButtonContent | null {
  return requireClient('preorderButtonFor').preorderButtonFor(variantId)
}

/**
 * One-call preorder widget (badge + CTA button, optional notify-me fallback)
 * using the auto-initialized client. The drop-in head-embed entry point.
 */
export function renderPreorderWidget(
  variant: VariantInput,
  options: PreorderWidgetOptions,
): Promise<WidgetHandle> {
  return requireClient('renderPreorderWidget').renderPreorderWidget(variant, options)
}

/** Renders the preorder CTA button into a container using the auto-initialized client. */
export function renderPreorderButton(
  variant: VariantInput,
  options: PreorderButtonWidgetOptions,
): Promise<WidgetHandle> {
  return requireClient('renderPreorderButton').renderPreorderButton(variant, options)
}

/** Renders the preorder badge into a container using the auto-initialized client. */
export function renderPreorderBadge(
  variant: VariantInput,
  options: PreorderBadgeWidgetOptions,
): Promise<WidgetHandle> {
  return requireClient('renderPreorderBadge').renderPreorderBadge(variant, options)
}

/** Renders the notify-me widget into a container using the auto-initialized client. */
export function renderNotifyMe(
  variant: VariantInput,
  options: NotifyMeWidgetOptions,
): Promise<WidgetHandle> {
  return requireClient('renderNotifyMe').renderNotifyMe(variant, options)
}

/** Shapes the theme embed's positional-args API passes around. */
interface CompatProductData {
  id?: number | string
  title?: string
  featured_image?: string
  variants?: Array<{
    id?: number | string
    title?: string
    featured_image?: { src?: string } | null
  }>
}

interface CompatCustomerData {
  email?: string
  phone?: string
  country_code?: string | number
}

function compatOptions(
  productData?: CompatProductData | null,
  variantId?: number | string | null,
  customerData?: CompatCustomerData | null,
): ModalOptions | null {
  const resolvedVariantId = variantId ?? productData?.variants?.[0]?.id
  if (resolvedVariantId === undefined || resolvedVariantId === null) {
    console.warn('[Stoq] _RestockRocket shim: a variantId (or productData.variants) is required')
    return null
  }

  const variant = productData?.variants?.find((v) => String(v.id) === String(resolvedVariantId))

  let prefill: ModalPrefill | undefined
  if (customerData) {
    prefill = {}
    if (customerData.email) prefill.email = customerData.email
    if (customerData.phone) {
      // The embed collects national number + country code separately.
      prefill.phone = customerData.country_code
        ? `+${customerData.country_code}${customerData.phone}`
        : customerData.phone
    }
  }

  const options: ModalOptions = {
    variantId: resolvedVariantId,
    // The _RestockRocket shim mirrors the theme embed, which always has
    // product context; tolerate absence at runtime (backend will reject).
    productId: productData?.id as number | string,
  }
  if (productData?.title !== undefined) options.productTitle = productData.title
  if (variant?.title !== undefined) options.variantTitle = variant.title
  const imageUrl = variant?.featured_image?.src ?? productData?.featured_image
  if (imageUrl) options.imageUrl = imageUrl
  if (prefill) options.prefill = prefill
  return options
}

/**
 * Installs the documented `window._RestockRocket` JavaScript API
 * (api_docs/javascript-api.mdx) backed by the SDK — only when the theme app
 * embed has not already defined it.
 */
function installCompatShim(initialized: StoqClient): void {
  /* c8 ignore next */
  if (typeof window === 'undefined') return
  const w = window as unknown as Record<string, unknown>
  if (w['_RestockRocket'] !== undefined) return // Theme embed wins.

  w['_RestockRocket'] = {
    openModal: (
      productData?: CompatProductData | null,
      variantId?: number | string | null,
      customerData?: CompatCustomerData | null,
    ): void => {
      const options = compatOptions(productData, variantId, customerData)
      if (options) initialized.openModal(options)
    },
    openInlineForm: (
      productData?: CompatProductData | null,
      variantId?: number | string | null,
      customerData?: CompatCustomerData | null,
      inlineFormContainer?: string | null,
      inlineFormContainerInsertType?: InlineFormOptions['insert'] | null,
    ): void => {
      const options = compatOptions(productData, variantId, customerData)
      if (!options) return
      if (!inlineFormContainer) {
        console.warn('[Stoq] _RestockRocket shim: openInlineForm requires a container selector')
        return
      }
      initialized.openInlineForm({
        ...options,
        container: inlineFormContainer,
        insert: inlineFormContainerInsertType ?? undefined,
      })
    },
    removeInlineForm: (): void => {
      removeInlineFormImpl()
    },
    getSellingPlan: (variantId: number | string) => initialized.sellingPlanFor(variantId),
    // SDK render widgets (no theme-embed equivalent — these are headless-only
    // conveniences so `window._RestockRocket.renderPreorderWidget()` works on
    // SDK-only storefronts).
    renderPreorderWidget: (variant: VariantInput, options: PreorderWidgetOptions) =>
      initialized.renderPreorderWidget(variant, options),
    renderPreorderButton: (variant: VariantInput, options: PreorderButtonWidgetOptions) =>
      initialized.renderPreorderButton(variant, options),
    renderPreorderBadge: (variant: VariantInput, options: PreorderBadgeWidgetOptions) =>
      initialized.renderPreorderBadge(variant, options),
    renderNotifyMe: (variant: VariantInput, options: NotifyMeWidgetOptions) =>
      initialized.renderNotifyMe(variant, options),
  }
}

function autoInit(): void {
  /* c8 ignore next */
  if (typeof document === 'undefined') return

  // document.currentScript is only reliable synchronously during script
  // evaluation, so capture the dataset now and init later.
  const script = document.currentScript
  if (!script || !(script instanceof HTMLScriptElement)) return

  const { shop, host, market, locale, storefrontToken, storefrontApiVersion, namespace } =
    script.dataset
  if (!shop) return // No data-shop: library-only mode, skip auto-init.
  if (!storefrontToken) {
    console.error(
      '[Stoq] auto-init needs data-storefront-token (the shop\'s PUBLIC Storefront API '
        + 'access token) alongside data-shop',
    )
    return
  }

  const run = (): void => {
    void init({ shop, storefrontToken, host, market, locale, storefrontApiVersion, namespace })
      .then((initialized) => {
        client = initialized
        installCompatShim(initialized)
        // Register the <stoq-*> custom elements against the auto-init client so
        // script-tag storefronts get them with zero extra code.
        defineStoqElements(initialized)
      })
      .catch((error: unknown) => {
        if (typeof console !== 'undefined') console.error('[Stoq] auto-init failed:', error)
      })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true })
  } else {
    run()
  }
}

autoInit()
